import React, { useMemo } from 'react';

import { WithAlloySuspense } from '@adsk/alloy-react-suspense';

import ConnectedReferencePicker, {
  ConnectedReferencePickerProps,
} from './ConnectedReferencePicker';
import { ReferencePickerPluginsContextProp } from './providers/ReferencePickerPluginsProvider';
import ReferencePickerProvider from './providers/ReferencePickerProvider';
import {
  Relationship,
  ResolvableCommonReferencePickerPlugin,
} from './types/types';

type ReferencePickerProps = Omit<
  ConnectedReferencePickerProps,
  'disableRelationshipService' | 'targetPlugins'
> & {
  targetPlugins: ResolvableCommonReferencePickerPlugin[];
  onPluginsLoadError?: ReferencePickerPluginsContextProp['onPluginsLoadError'];
  relationshipEntitiesList?: Relationship[];
};

/**
 * A ReferencePicker component.
 * This component allows creating references between two entities.
 * When using this component, use targetPlugins to determine which entity types you want to allow linking to.
 * this entity uses Relationship Service as the BE for saving relationships.
 *
 * If you want to add support for a new type, follow: https://git.autodesk.com/BIM360/acs-sc-web-platform/blob/master/libs/reference-picker/ADD_NEW_PLUGIN.md
 */
const ReferencePicker = React.forwardRef<HTMLDivElement, ReferencePickerProps>(
  (props, ref) => {
    const {
      sourceEntityId,
      sourceEntityDomain,
      sourceEntityType,
      relationshipEntitiesList,
      onPluginsLoadError,
    } = props;
    const sourceIdAsArr = useMemo(
      () => (sourceEntityId ? [sourceEntityId] : []),
      [sourceEntityId],
    );
    const disableRelationshipService = 'relationshipEntitiesList' in props;

    const { targetPlugins, ...rest } = props;

    return (
      <ReferencePickerProvider
        sourceEntityIds={sourceIdAsArr}
        sourceEntityDomain={sourceEntityDomain}
        sourceEntityType={sourceEntityType}
        plugins={targetPlugins}
        relationshipEntitiesList={relationshipEntitiesList}
        disableRelationshipService={disableRelationshipService}
        onPluginsLoadError={onPluginsLoadError}
      >
        <ConnectedReferencePicker
          {...rest}
          disableRelationshipService={disableRelationshipService}
          ref={ref}
        />
      </ReferencePickerProvider>
    );
  },
);

ReferencePicker.displayName = 'ReferencePicker';

export default WithAlloySuspense(ReferencePicker);
